import React, { useEffect, useState } from 'react';
import { Alert, DatePicker, Form, Input, Modal, Select, Spin, message } from 'antd';
import moment, { Moment } from 'moment';
import {
	getMemberAdSlotPackage,
	grantMemberAdSlotPackage,
	MemberAdSlotPackage,
} from '../../service/user/UserService';

interface FormValues {
	slotLimit: 5 | 10 | 20 | 30 | 50;
	durationMonths: 1 | 6 | 12;
	expiresAt?: Moment;
	reference: string;
}

interface Props {
	currentUser: any;
	isModalOpen: boolean;
	onClose: () => void;
	onGranted: () => Promise<any> | void;
}

const createRequestId = () => {
	if (window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}
	return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export default function PopupMemberAdSlotPackage({ currentUser, isModalOpen, onClose, onGranted }: Props) {
	const [form] = Form.useForm<FormValues>();
	const selectedSlotLimit = Form.useWatch('slotLimit', form);
	const selectedDurationMonths = Form.useWatch('durationMonths', form);
	const selectedExpiresAt = Form.useWatch('expiresAt', form);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [activePackage, setActivePackage] = useState<MemberAdSlotPackage | null>(null);
	const [requestId, setRequestId] = useState(createRequestId());

	useEffect(() => {
		if (!isModalOpen || !currentUser?._id) return;
		setRequestId(createRequestId());
		form.resetFields();
		setLoading(true);
		getMemberAdSlotPackage(currentUser._id)
			.then(res => {
				const currentPackage = res.data?.data?.activePackage || null;
				setActivePackage(currentPackage);
			})
			.catch((error: any) => {
				setActivePackage(null);
				message.error(error?.response?.data?.message || 'Unable to load the current package.');
			})
			.finally(() => setLoading(false));
	}, [currentUser?._id, form, isModalOpen]);

	const submit = async () => {
		try {
			const values = await form.validateFields();
			setSubmitting(true);
			const response = await grantMemberAdSlotPackage({
				userId: currentUser._id,
				slotLimit: values.slotLimit,
				durationMonths: values.durationMonths,
				expiresAt: values.expiresAt?.endOf('day').toISOString(),
				reference: values.reference.trim(),
				requestId,
			});
			message.success(response.data?.message || 'Member ad-free package granted successfully.');
			await onGranted();
			onClose();
		} catch (error: any) {
			if (error?.errorFields) return;
			message.error(error?.response?.data?.message || 'Unable to grant the package.');
		} finally {
			setSubmitting(false);
		}
	};

	const estimatedExpiration = (() => {
		if (selectedExpiresAt) return moment(selectedExpiresAt).endOf('day');
		if (!selectedDurationMonths) return null;
		const isRenewal = activePackage?.slotLimit === selectedSlotLimit;
		const baseDate = isRenewal ? moment(activePackage.expiredAt) : moment();
		return baseDate.add(selectedDurationMonths, 'months');
	})();
	const packageAction = !activePackage
		? 'New package'
		: activePackage.slotLimit === selectedSlotLimit
		? 'Renewal'
		: 'Upgrade';

	return (
		<Modal
			title="MEMBER AD-FREE PACKAGE"
			visible={isModalOpen}
			onCancel={onClose}
			onOk={submit}
			confirmLoading={submitting}
			okText="Confirm package"
			okButtonProps={{ style: { backgroundColor: '#13ae81', borderColor: '#13ae81' } }}
			destroyOnClose
			centered
		>
			<Spin spinning={loading}>
				<div className="mb-4 rounded border border-gray-200 bg-gray-50 p-3">
					<div className="mb-1">Account: <strong>{currentUser?.email || currentUser?.tmp_email}</strong></div>
					<div>
						EzWork management plan: <strong>{String(currentUser?.current_plan || 'free').toUpperCase()}</strong>
						{currentUser?.plan_expired_time
							? ` · expires ${moment(currentUser.plan_expired_time).format('DD/MM/YYYY')}`
							: ''}
					</div>
				</div>
				<div className="mb-2 font-semibold">Current member ad-free package</div>
				{activePackage ? (
					<Alert
						type="info"
						showIcon
						className="mb-4"
						message={`${activePackage.slotLimit}-employee package`}
						description={`${activePackage.usedSlots}/${activePackage.slotLimit} employees selected · expires ${moment(
							activePackage.expiredAt,
						).format('DD/MM/YYYY HH:mm')}`}
					/>
				) : (
					<Alert
						type="warning"
						showIcon
						className="mb-4"
						message="No active member ad-free package"
						description="The EzWork management plan above is separate from the employee ad-free package."
					/>
				)}

				<Form form={form} layout="vertical">
					<Form.Item name="slotLimit" label="Ad-free employee slots" rules={[{ required: true }]}> 
						<Select placeholder="Select employee slots">
							{[5, 10, 20, 30, 50].map(value => (
								<Select.Option key={value} value={value}>{value} employees</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item name="durationMonths" label="Package period" rules={[{ required: true }]}> 
						<Select placeholder="Select package period">
							<Select.Option value={1}>1 month</Select.Option>
							<Select.Option value={6}>6 months</Select.Option>
							<Select.Option value={12}>12 months</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item name="expiresAt" label="Exact expiration date (optional)">
						<DatePicker
							style={{ width: '100%' }}
							format="DD/MM/YYYY"
							disabledDate={date => !!date && date < moment().startOf('day')}
						/>
					</Form.Item>
					{selectedSlotLimit && selectedDurationMonths ? (
						<Alert
							type="success"
							showIcon
							className="mb-4"
							message={`${packageAction}: ${selectedSlotLimit}-employee package`}
							description={`${selectedDurationMonths} months · estimated expiration ${
								estimatedExpiration ? estimatedExpiration.format('DD/MM/YYYY HH:mm') : '-'
							}`}
						/>
					) : (
						<Alert
							type="info"
							showIcon
							className="mb-4"
							message="Select employee slots and package period"
						/>
					)}
					<Form.Item
						name="reference"
						label="Payment or support reference"
						rules={[
							{ required: true, whitespace: true, message: 'Please enter a payment or support reference.' },
							{ max: 500, message: 'Reference must not exceed 500 characters.' },
						]}
					>
						<Input.TextArea rows={3} placeholder="Example: Customer paid via support, receipt ABC123" />
					</Form.Item>
				</Form>
			</Spin>
		</Modal>
	);
}
